// Angular
import { ChangeDetectionStrategy, Component, forwardRef, Injector, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
// BCDFX - Globals
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// Services
// Components
// Models
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
// Enums - Coordination
// Enums - ATDL
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
// Enums

import { LoggerService } from "@bfx/logger";
import { NumberSpinnerComponent, ZERO } from "shared/base-components/number-spinner.component";
import { AmountSpinnerComponent } from "shared/base-components/amount-spinner.component";
import { ICDBaseSpinnerComponent } from "app/order-entry/interface/dynamic-controls/icd-base-spinner.component";
import { BfxInputDirective } from "../../../../shared/base-components/bfx-input";
import { ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
const logger = LoggerService.for(__dirname);

export const DYN_AMOUNT_SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDAmountSpinnerComponent),
  multi: true,
};

@Component({
  selector: "icd-amount-spinner",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__base__spinner__container]": "true",
  },
  templateUrl: "./html/icd-amount-spinner.html",
  providers: [DYN_AMOUNT_SPINNER_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDAmountSpinnerComponent
  extends ICDBaseSpinnerComponent
  implements ControlValueAccessor
{
  /**********************************************************************/
  /* DATA */

  private amountConfig: {
    considerOrderAmount: boolean;
    /** The amount on the parent Algo order - used to ensure parameter amts don't exceed parent's */
    orderAmount: CubiqTS.BigNumber;
  } = {
    considerOrderAmount: true,
    orderAmount: null,
  };

  // CONSTRAINTS
  public currency: string = null;

  /************************************/
  /* Element Ref */
  @ViewChild("amount_spinner", { static: true }) amountSpinner: AmountSpinnerComponent;

  inputDirective: BfxInputDirective;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
    this.inputDirective = injector.get(BfxInputDirective);
    this.initAmountConfiguration();
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API */

  protected override initializeICDSpecificModelProperties(): boolean {
    const error = super.initializeICDSpecificModelProperties();

    if (this.model instanceof ATDLInterfaceControlModel) {
      // ORDER AMOUNT CONSIDERATION - (to use it or not)
      this.cd.markForCheck();
      const isAmtType = this.model.paramType === ATDLNParameterType.AMT;
      this.amountConfig.considerOrderAmount = isAmtType;
    }
    return error;
  }

  protected override initConfigNegative(): void {
    this.cd.markForCheck();

    // DO not allow any negative configuration
    this.config.allowNegativeNum = false;
    return;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected override handleConstraintUpdate(data: any): void {
    let dynResponses = new Map<ICDNResponseCommand, any>();
    this.cd.markForCheck();

    if (
      data instanceof Object &&
      data.hasOwnProperty("value") &&
      data.hasOwnProperty("name") &&
      typeof data.name === "string"
    ) {
      if (data.name.toUpperCase() === "ccyCode".toUpperCase()) {
        if (typeof data.value === "string" && data.value.length === 3) {
          this.currency = data.value;
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Constraint '${data.name}' payload has invalid data.value! Data dump: %O`,
            data.value,
          );
        }
      } else if (
        data.name.toUpperCase() === "orderAmt".toUpperCase() &&
        (data.value instanceof CubiqTS.BigNumber || data.value === null)
      ) {
        if (this.amountConfig.considerOrderAmount) {
          this.outputExtendedLogging(`ICD ${this.icdId} received orderAmount :  %O`, data.value);
          this.amountConfig.orderAmount = data.value;
          this.validate();
        }
      } else {
        // STATE FLOW RULES
        dynResponses = this.processExternalStateFlowDependency(data, dynResponses);
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Invalid constraint data! Data dump: %O`,
        data,
      );
    }
    this.handleDynResponses(dynResponses);
  }

  /**
   * Perform any non-standard spinner validation in a subclass where the component is not focussed.
   * If this returns false the override fn should have aslo set the data.errorMsg to indicate what's wrong
   * @returns
   */
  protected override additionalSpinnerValidation(): boolean {
    let valid = true;
    if (
      this.amountConfig.considerOrderAmount &&
      this.subData.numValue instanceof CubiqTS.BigNumber &&
      this.amountConfig.orderAmount instanceof CubiqTS.BigNumber &&
      (<CubiqTS.BigNumber>this.subData.numValue).isGreaterThan(this.amountConfig.orderAmount)
    ) {
      this.cd.markForCheck();

      valid = false;
      this.data.errorMsg = `${
        this.label
      }  greater than order amount value of ${this.amountConfig.orderAmount.toFixed(
        this.config.precision,
      )} `;
    }
    return valid;
  }

  protected override getDefaultLabel(): string {
    return "Amount Spinner";
  }
  protected override getAllowedTypes(): ATDLNParameterType[] {
    return [ATDLNParameterType.AMT, ATDLNParameterType.QTY];
  }

  protected override get numberSpinner(): NumberSpinnerComponent {
    return this.amountSpinner;
  }

  protected override initialiseConfiguration(): void {
    this.cd.markForCheck();
    this.config.precision = 2;
    this.config.minValue = ZERO;
    this.config.minValueSet = true;
    this.config.maxValue = null;
    this.config.maxValueSet = false;
    this.config.emptyValue = ZERO;
    this.config.allowNegativeNum = false;
    this.initAmountConfiguration();
  }

  protected initAmountConfiguration(): void {
    if (this.amountConfig) {
      this.cd.markForCheck();
      this.amountConfig.orderAmount = null;
      this.amountConfig.considerOrderAmount = true;
    }
  }
}
